import { 
  getAnalytics, 
  logEvent as fbLogEvent, 
  setUserId as fbSetUserId,
  setUserProperties as fbSetUserProperties,
  isSupported, 
  Analytics 
} from 'firebase/analytics';
import { app, isFirebaseConfigured } from '../firebase';

let analyticsInstance: Analytics | null = null;

const isDebugMode = (): boolean => {
  if (import.meta.env.DEV) return true;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    return host.includes('stg') || host.includes('staging') || host === 'localhost' || host === '127.0.0.1';
  }
  return false;
};

if (isFirebaseConfigured) {
  isSupported().then((supported) => {
    if (supported && app) {
      analyticsInstance = getAnalytics(app);
      if (isDebugMode()) {
        console.log("📊 Inicjalizacja Firebase Analytics pomyślna.");
      }
    } else {
      if (isDebugMode()) {
        console.warn("⚠️ Firebase Analytics nie jest wspierany w tej przeglądarce.");
      }
    }
  }).catch((err) => {
    if (isDebugMode()) {
      console.error("⚠️ Błąd inicjalizacji Firebase Analytics:", err);
    }
  });
}

/**
 * Filtruje potencjalne wrażliwe dane (PII) z parametrów zdarzeń w celu ochrony prywatności (RODO / GDPR)
 */
const sanitizeParams = (params: Record<string, unknown>): Record<string, unknown> => {
  const sanitized = { ...params };
  const sensitiveKeys = ['email', 'password', 'phone', 'address'];
  
  sensitiveKeys.forEach((key) => {
    if (key in sanitized) {
      if (isDebugMode()) {
        console.warn(`⚠️ [Analytics PII Guard] Usunięto niedozwolony parametr '${key}' ze zdarzenia w celu ochrony prywatności (RODO).`);
      }
      delete sanitized[key];
    }
  });

  return sanitized;
};

/**
 * Ustawianie identyfikatora oraz cech użytkownika w GA4
 * @param userId Identyfikator użytkownika (UID) lub null w przypadku wylogowania
 * @param properties Cechy profilu użytkownika (np. profession, subscription_status)
 */
export const setAnalyticsUser = (userId: string | null, properties?: Record<string, unknown>): void => {
  if (analyticsInstance) {
    try {
      fbSetUserId(analyticsInstance, userId);
      if (properties) {
        const cleanProps = sanitizeParams(properties);
        fbSetUserProperties(analyticsInstance, cleanProps as Record<string, string>);
      }
      if (isDebugMode()) {
        console.log(`[Analytics] Ustawiono użytkownika: ${userId || 'anonimowy'}`, properties);
      }
    } catch (error) {
      if (isDebugMode()) {
        console.error(`[Analytics] Błąd ustawiania właściwości użytkownika:`, error);
      }
    }
  } else {
    if (isDebugMode()) {
      console.log(`[Analytics - OFFLINE/DEMO] Ustawiono użytkownika: ${userId || 'anonimowy'}`, properties);
    }
  }
};

/**
 * Bezpieczne logowanie zdarzeń (działa również w trybie DEMO/bez Firebase)
 * @param eventName Nazwa zdarzenia
 * @param params Dodatkowe parametry zdarzenia
 */
export const logEvent = (eventName: string, params: Record<string, unknown> = {}): void => {
  const safeParams = sanitizeParams(params);
  if (analyticsInstance) {
    try {
      fbLogEvent(analyticsInstance, eventName, safeParams);
      if (isDebugMode()) {
        console.log(`[Analytics] Zdarzenie: ${eventName}`, safeParams);
      }
    } catch (error) {
      if (isDebugMode()) {
        console.error(`[Analytics] Błąd logowania zdarzenia ${eventName}:`, error);
      }
    }
  } else {
    // W trybie DEMO lub przed załadowaniem logujemy do konsoli deweloperskiej
    if (isDebugMode()) {
      console.log(`[Analytics - OFFLINE/DEMO] Zdarzenie: ${eventName}`, safeParams);
    }
  }
};

/**
 * Logowanie wyświetlenia strony (Page View)
 * @param pagePath Ścieżka strony
 * @param pageTitle Tytuł strony (opcjonalny)
 */
export const logPageView = (pagePath: string, pageTitle?: string): void => {
  logEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href
  });
};

/**
 * Logowanie udostępniania zasobów (Standardowe zdarzenie GA4 `share`)
 */
export const logShare = (contentType: string, itemId: string, method: string = 'link_copy'): void => {
  logEvent('share', {
    method,
    content_type: contentType,
    item_id: itemId
  });
};

/**
 * Logowanie wyjątków i błędów (Standardowe zdarzenie GA4 `exception`)
 */
export const logException = (description: string, fatal: boolean = false): void => {
  logEvent('exception', {
    description,
    fatal
  });
};

/**
 * Logowanie wyszukiwania (Standardowe zdarzenie GA4 `search`)
 */
export const logSearch = (searchTerm: string): void => {
  if (!searchTerm.trim()) return;
  logEvent('search', {
    search_term: searchTerm
  });
};

